/**
 * autoplayResponsiveScale
 * -----------------------
 * Fonte única de verdade pra escalar o conteúdo do autoplay-overlay (fontes,
 * ícones, gaps, paddings) com base no tamanho FÍSICO em pixels da caixa do
 * overlay — não na % relativa ao container.
 *
 * Por que px e não %: o mesmo widthPercent gera overlays fisicamente
 * diferentes num player de 300px vs. 1200px. Escala em % não reflete o
 * espaço real que o conteúdo tem.
 *
 * Usado por:
 *   • AutoplayContent (preview/editor React)
 *   • AutoplayOverlay (preview/editor React)
 *   • e-player-js/js/runner.js (runtime CDN) — espelhado inline. Manter os
 *     dois em sincronia byte-a-byte.
 *
 * Matemática pura. Sem React. Números entram, números saem.
 */
import { getBreakpoint, type Breakpoint, clamp } from './autoplayOverlayGeometry';

export interface ResponsiveScaleInput {
  /** Largura medida do overlay em px (vinda do ResizeObserver). */
  overlayWidthPx: number;
  /** Altura medida do overlay em px. */
  overlayHeightPx: number;
  /** Largura do container do player em px — usada pra detectar breakpoint. */
  containerWidthPx: number;
  /** Id do template ('autoPlay', 'small', 'large', 'transparent', ...). */
  templateId: string;
}

export interface ResponsiveScales {
  /** Multiplicador aplicado ao font-size dos textos. */
  fontScale: number;
  /** Multiplicador aplicado às dimensões dos ícones. */
  iconScale: number;
  /** Multiplicador aplicado ao gap entre elementos do conteúdo. */
  gapScale: number;
  /** Multiplicador aplicado ao padding interno. */
  paddingScale: number;
}

/**
 * Largura (em px) na qual fontScale/iconScale = 1.0 pra cada template.
 * Calibrada pra que a aparência default bata com o layout legado quando o
 * overlay está no tamanho % padrão num player desktop 16:9 típico.
 */
const REFERENCE_WIDTH_PX: Record<string, number> = {
  autoPlay: 280,
  small: 200,
  large: 240,
  transparent: 240,
};

/**
 * Razão altura/largura ideal por template. Quando a razão real desvia,
 * a fonte é amortecida (caixas muito esticadas significam menos ar vertical
 * pro conteúdo respirar).
 */
const IDEAL_ASPECT_RATIO: Record<string, number> = {
  autoPlay: 0.4,
  small: 0.2,
  large: 0.4,
  transparent: 0.4,
};

/**
 * Piso de escala por breakpoint. Mobile tem piso maior pra manter o texto
 * legível (fonte base 16px × 0.7 = ~11px, acima do limite de legibilidade).
 */
const BREAKPOINT_MIN_SCALE: Record<Breakpoint, number> = {
  mobile: 0.70,
  tablet: 0.65,
  desktop: 0.55,
};

/** Tetos máximos. */
const FONT_MAX = 2.2;
const ICON_MAX_DEFAULT = 2.0;
const ICON_MAX_LARGE = 2.5;
const GAP_MIN = 0.5;
const GAP_MAX = 1.8;
const PAD_MIN = 0.5;
const PAD_MAX = 1.5;

const DEFAULT_TEMPLATE = 'autoPlay';

export const DEFAULT_SCALES: ResponsiveScales = Object.freeze({
  fontScale: 1.0,
  iconScale: 1.0,
  gapScale: 1.0,
  paddingScale: 1.0,
});

const MEMO_LIMIT = 64;
const memoCache = new Map<string, ResponsiveScales>();

function buildKey(input: ResponsiveScaleInput): string {
  // Quantiza px pra reduzir cache thrash durante drags de resize suaves.
  const wQ = Math.round(input.overlayWidthPx / 4) * 4;
  const hQ = Math.round(input.overlayHeightPx / 4) * 4;
  const cQ = Math.round(input.containerWidthPx / 8) * 8;
  return `${input.templateId}|${wQ}|${hQ}|${cQ}`;
}

function computeScales(input: ResponsiveScaleInput): ResponsiveScales {
  const tpl = REFERENCE_WIDTH_PX[input.templateId] != null
    ? input.templateId
    : DEFAULT_TEMPLATE;

  const refWidth = REFERENCE_WIDTH_PX[tpl];
  const idealAspect = IDEAL_ASPECT_RATIO[tpl];

  // 1. Escala base a partir dos px físicos (relativa à referência do template).
  const basePxScale = input.overlayWidthPx > 0
    ? input.overlayWidthPx / refWidth
    : 1.0;

  // 2. Penalidade de aspect — quando a caixa tá muito mais alta/baixa que o
  //    ideal, amortece a fonte pra dar mais ar pro conteúdo.
  const actualAspect = input.overlayHeightPx > 0 && input.overlayWidthPx > 0
    ? input.overlayHeightPx / input.overlayWidthPx
    : idealAspect;
  const aspectDelta = Math.abs(actualAspect - idealAspect) / idealAspect;
  const aspectPenalty = clamp(1 - 0.15 * aspectDelta, 0.85, 1.0);

  // 3. Piso de escala por breakpoint pra garantir legibilidade.
  const bp = getBreakpoint(input.containerWidthPx);
  const minScale = BREAKPOINT_MIN_SCALE[bp];

  // 4. Teto de ícone específico por template (CirclePlay do 'large' pode
  //    crescer mais que ícone decorativo dos outros).
  const iconCap = tpl === 'large' ? ICON_MAX_LARGE : ICON_MAX_DEFAULT;

  // 5. Escala de ícone usa base direta pro 'large' (ícone é o protagonista),
  //    senão é levemente amortecida pra ícone não dominar o texto nos
  //    templates compactos.
  const iconBase = tpl === 'large' ? basePxScale : basePxScale * 0.9;

  return {
    fontScale: clamp(basePxScale * aspectPenalty, minScale, FONT_MAX),
    iconScale: clamp(iconBase, minScale, iconCap),
    gapScale: clamp(basePxScale, GAP_MIN, GAP_MAX),
    paddingScale: clamp(basePxScale, PAD_MIN, PAD_MAX),
  };
}

/**
 * Calcula as escalas responsivas pro conteúdo do autoplay overlay.
 * Memoizada; pura; seguro chamar a cada frame durante resize.
 */
export function computeAutoplayResponsiveScales(
  input: ResponsiveScaleInput,
): ResponsiveScales {
  const key = buildKey(input);
  const cached = memoCache.get(key);
  if (cached) {
    memoCache.delete(key);
    memoCache.set(key, cached);
    return cached;
  }
  const result = Object.freeze(computeScales(input)) as ResponsiveScales;
  memoCache.set(key, result);
  if (memoCache.size > MEMO_LIMIT) {
    const oldest = memoCache.keys().next().value;
    if (oldest !== undefined) memoCache.delete(oldest);
  }
  return result;
}

/**
 * Fallback legado: calcula um scaleFactor único a partir do widthPercent
 * salvo. Usado como valor inicial antes do ResizeObserver disparar, pra
 * evitar "flash" visual. Espelha o comportamento pré-refactor.
 */
export function legacyScaleFromWidthPercent(
  savedWidthPercent: number | undefined,
  defaultWidthPercent: number,
  templateId: string,
): number {
  if (savedWidthPercent == null) return 1.0;
  const raw = savedWidthPercent / defaultWidthPercent;
  // 'small' não deve crescer além de 1x quando expandido horizontalmente —
  // mantém a decisão legada do AutoplayOverlay.
  const upper = templateId === 'small' ? 1.0 : 1.8;
  return clamp(raw, 0.65, upper);
}
